import User from "./user";
import Msg from "./message";
import FileServer from "./file-server";
import AppSettings from "./app-settings";
import Api from "./api";
import { compressImage, isBase64String, sortBy } from "../utils";
import IUserApplication from "../interface/user-application";
import ICallUser from "../interface/call-user";
import IAppUser from "../interface/app-user";
import IAppSetting from "../interface/app-setting";
import IApiUser from "../interface/api/api-user";
import IApiApplication from "../interface/api/api-application";
import Global from "../global";
import Permission from "../enum/permission";
import Message from "../enum/message";
import ErrorCode from "../enum/error-code";
import AppSetting from "../enum/app-setting";

class ApplicationsInstance {

    private UserApplication: IUserApplication | null = null;
    
    private IsAuthorized: boolean = false;

    private LoadedApp: IApiApplication | null = null;

    /* Public Methods */

    public getId(): string {
        if (this.UserApplication) {
            return this.UserApplication.id;
        }
        return '';
    }

    public getName(): string | null {
        if (this.UserApplication) {
            return this.UserApplication.name;
        }
        return null;
    }

    public async rename(name: string): Promise<string> {
        if (this.UserApplication) {
            name = name.trim();
            if (name.length) {
                const app: IApiApplication = await this.load();
                app.description = name;
                await Api.appSave(app);
                this.UserApplication.name = name;
                return name;
            } else {
                throw ErrorCode.BadArguments;
            }
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public getLogo(): string {
        if (this.UserApplication && this.UserApplication.logo) {
            return FileServer.getFileUrl(this.UserApplication.logo);
        }
        return '';
    }

    public async setLogo(value: string): Promise<string> {
        let logo: string = `${value}`;
        if (this.UserApplication && (!logo || isBase64String(logo))) {
            const app: IApiApplication = await this.load();
            if (logo) {
                logo = await compressImage(logo, Global.IMAGE_COMPRESS_SCALE);
            }
            const previous: string = app.logo;
            app.logo = await FileServer.saveAppLogo(this.getId(), logo);
            await Api.appSave(app);
            this.UserApplication.logo = app.logo;
            if (previous) {
                await FileServer.delete(previous);
            }
            return this.getLogo();
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public getBrandColor(): string {
        return AppSettings.get(AppSetting.BrandColor) || '';
    }

    public async setBrandColor(color: string): Promise<string> {
        const setting: IAppSetting<string> = await AppSettings.set(AppSetting.BrandColor, color);
        return setting.value;
    }

    public async getPublicEnabled(): Promise<boolean> {
        const app: IApiApplication = await this.load();
        const publicUserName: string = this.getPublicUserName();
        return !!app.access.find((u: IApiUser) => u.userName === publicUserName);
    }

    public async getCallUsers(): Promise<ICallUser[]> {
        const support: ICallUser = {
            name: Msg.get(Message.supportname),
            callUrl: Global.SUPPORT_ADDRESS
        };
        return [support, ...Api.getCallUrl()].filter((url: ICallUser) => {
            return url.name !== User.getName();
        });
    }

    public async getUsers(): Promise<IAppUser[]> {
        const hidden: string[] = [User.getName(), this.getPublicUserName()];
        const app: IApiApplication = await this.load();
        let result: IAppUser[] = app.access.map((user: IApiUser) => {
            return this.toAppUser(user, app.ownerUserId);
        });
        result = result.filter((user: IAppUser) => !hidden.includes(user.name));
        return sortBy(result, 'name');
    }

    public async setUsers(users: IAppUser[]): Promise<IAppUser[]> {
        for (let i = 0; i < users.length; i++) {
            await this.attachUser(users[i]);
        }
        return this.getUsers();
    }

    public async attachUser(user: IAppUser): Promise<IAppUser> {
        const app: IApiApplication = await this.load();
        await Api.grantAccess(app, {
            accessLevel: user.permissions,
            userName: user.name,
            userId: user.id,
            jSon: {
                disabledModules: user.disabledModules,
                disabledTables: user.disabledTables
            }
        });
        return await this.getUser(user.name) as IAppUser;
    }

    public async detachUser(username: string): Promise<string> {
        const app: IApiApplication = await this.load();
        const user: IAppUser | null = await this.getUser(username);
        if (user) {
            await Api.revokeAccess(app, user.id);
        }
        return username;
    }

    public async setOwner(id: number): Promise<IAppUser[]> {
        const users: IAppUser[] = await this.getUsers();
        const user: IAppUser | null = users.find((u: IAppUser) => u.id === id) || null;
        if (user) {
            const app: IApiApplication = await this.load();
            app.ownerUserId = id;
            await Api.appSave(app);
            this.LoadedApp = null;
            return this.getUsers();
        } else {
            throw ErrorCode.NoUser;
        }
    }

    /* Private Public Methods */

    public async setPublicEnabled(enabled: boolean): Promise<boolean> {
        const username: string = this.getPublicUserName();
        if (enabled) {
            try {
                await Api.createUser(username, btoa(username));
            } catch (error) {
                if (error !== ErrorCode.UserExists) {
                    throw error;
                }
            }
            this.attachUser({
                id: 0,
                name: username,
                isOwner: false,
                permissions: Permission.Read | Permission.Write | Permission.Delete,
                disabledTables: [],
                disabledModules: []
            });
        } else {
            await this.detachUser(username);
        }
        return await this.getPublicEnabled();
    }

    public isOwner(): boolean {
        if (this.UserApplication) {
            return this.UserApplication.isOwner;
        }
        return false;
    }

    public setApplication(application: IUserApplication): void {
        this.UserApplication = application;
        this.IsAuthorized = true;
        this.LoadedApp = null;
    }

    public isAuthorized(): boolean {
        return this.IsAuthorized;
    }

    public logout(): void {
        this.UserApplication = null;
        this.IsAuthorized = false;
        this.LoadedApp = null;
    }

    public async load(): Promise<IApiApplication> {
        if (!this.LoadedApp) {
            const app: IApiApplication | null = await Api.appLoad(this.getId());
            if (app) {
                this.LoadedApp = app;
            } else {
                throw ErrorCode.ApiInternal;
            }
        }
        return this.LoadedApp;
    }

    public getPublicUserName(): string {
        return `${this.getId()}${Global.PUBLIC_USER_SUFFIX}`;
    }

    /* Private Methods */

    private async getUser(name: string): Promise<IAppUser | null> {
        const app: IApiApplication = await this.load();
        const user: IApiUser | null = app.access.find((u: IApiUser) => u.userName === name) || null;
        if (user) {
            return this.toAppUser(user);
        }
        return null;
    }

    private toAppUser(user: IApiUser, ownerId: number = 0): IAppUser {
        return {
            id: user.userId,
            name: user.userName,
            isOwner: user.userId === ownerId,
            permissions: user.accessLevel,
            disabledTables: (user.jSon && user.jSon.disabledTables) ? user.jSon.disabledTables : [],
            disabledModules: (user.jSon && user.jSon.disabledModules) ? user.jSon.disabledModules : []
        } as IAppUser;
    }

}

const Application = new ApplicationsInstance();

export default Application;